/**
 * Calendario de feriados.
 *
 * No es decorativo: la pestaña de novedades lo usa para separar el día hábil
 * del sábado o feriado, y de esa separación depende a qué concepto de Visma
 * van los pallets entregados de Preventa. Un feriado que falta se liquida como
 * día hábil.
 *
 * Un feriado es **día y mes**: Navidad es el 25/12 de todos los años y se
 * carga una sola vez. Los móviles —Carnaval, Semana Santa, los puentes— caen
 * en otra fecha cada año y se cargan para el año que corresponden.
 *
 * El calendario es uno solo para todas las operaciones (22-09-2026).
 */

import { useCallback, useEffect, useState } from 'react';

import { CalendarPlus, Loader2, Trash2 } from 'lucide-react';

import { mensajeDe, type Feriado } from '../../api';
import { Aviso } from '../../components/Aviso';
import { Modal } from '../../components/Modal';
import { EnvoltorioTabla, FilaVacia } from '../../components/Tabla';
import { agregarFeriado, borrarFeriado, listarFeriados } from './api';

const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'] as const;

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
] as const;

const p2 = (n: number): string => String(n).padStart(2, '0');

/** "25/12" para los de todos los años, "03/04/2026" para los de un año. */
const formatear = (f: Feriado): string =>
  f.anio === null ? `${p2(f.dia)}/${p2(f.mes)}` : `${p2(f.dia)}/${p2(f.mes)}/${String(f.anio)}`;

/**
 * Cómo cae el feriado en el año que se está mirando. La fecha se arma con sus
 * partes y no con `new Date(texto)`, que la leería como UTC y correría el día.
 */
function comoCae(f: Feriado, anioMostrado: number): string {
  const anio = f.anio ?? anioMostrado;
  const fecha = new Date(anio, f.mes - 1, f.dia);
  // Un 29 de febrero recurrente no existe en un año que no es bisiesto.
  if (fecha.getMonth() !== f.mes - 1) return '—';
  return DIAS[fecha.getDay()] ?? '';
}

export function PanelFeriados(): React.JSX.Element {
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [feriados, setFeriados] = useState<readonly Feriado[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [alta, setAlta] = useState(false);
  const [aBorrar, setABorrar] = useState<Feriado | null>(null);

  const cargar = useCallback(async (): Promise<void> => {
    setCargando(true);
    try {
      setFeriados(await listarFeriados(anio));
      setError('');
    } catch (err) {
      setFeriados([]);
      setError(mensajeDe(err));
    } finally {
      setCargando(false);
    }
  }, [anio]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  /** Toda mutación pasa por acá: ejecuta, recarga y deja el resultado a la vista. */
  async function operar(fn: () => Promise<unknown>, exito: string): Promise<void> {
    try {
      await fn();
      setAviso(exito);
      setError('');
      await cargar();
    } catch (err) {
      setError(mensajeDe(err));
      setAviso('');
    }
  }

  const moviles = feriados.filter((f) => f.anio !== null).length;

  return (
    <section className="tarjeta">
      <h2>Feriados</h2>
      <p className="nota">
        Las novedades de Preventa se liquidan en un concepto distinto si el viaje cayó en sábado o
        feriado, así que un feriado que falte se cuenta como día hábil.
      </p>

      {error !== '' && <Aviso tono="error">{error}</Aviso>}
      {aviso !== '' && <Aviso tono="ok">{aviso}</Aviso>}

      <div className="banda">
        <label className="campo">
          Año
          <input
            type="number"
            min={2000}
            max={2100}
            value={anio}
            onChange={(ev) => setAnio(Number(ev.target.value))}
          />
        </label>
        <button type="button" className="primaria" onClick={() => setAlta(true)}>
          <CalendarPlus size={16} aria-hidden="true" />
          Agregar feriado
        </button>
      </div>

      {!cargando && moviles === 0 && feriados.length > 0 && (
        <Aviso tono="atencion">
          {`No hay ningún feriado propio de ${String(anio)}. Carnaval, Viernes Santo y los feriados con fines turísticos cambian de fecha cada año: hay que cargarlos.`}
        </Aviso>
      )}

      <EnvoltorioTabla
        pie={`${String(feriados.length)} feriado(s) en ${String(anio)}, de los cuales ${String(moviles)} son propios del año`}
      >
        <table className="tabla">
          <thead>
            <tr>
              <th scope="col">Fecha</th>
              <th scope="col">Día</th>
              <th scope="col">Vigencia</th>
              <th scope="col">Descripción</th>
              <th scope="col">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {cargando && (
              <FilaVacia columnas={5}>
                <Loader2 className="girando" size={16} aria-hidden="true" /> Cargando…
              </FilaVacia>
            )}
            {!cargando && feriados.length === 0 && (
              <FilaVacia columnas={5}>
                {`No hay feriados cargados para ${String(anio)}. Agregarlos antes de liquidar el período.`}
              </FilaVacia>
            )}
            {!cargando &&
              feriados.map((f) => (
                <tr key={f.clave}>
                  <td className="num">{formatear(f)}</td>
                  <td>{comoCae(f, anio)}</td>
                  <td>{f.anio === null ? 'Todos los años' : `Solo ${String(f.anio)}`}</td>
                  <td>{f.descripcion}</td>
                  <td>
                    <button
                      type="button"
                      className="plano"
                      onClick={() => setABorrar(f)}
                      aria-label={`Quitar el feriado del ${formatear(f)}`}
                    >
                      <Trash2 size={15} aria-hidden="true" />
                      Quitar
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </EnvoltorioTabla>

      {alta && (
        <DialogoAlta
          anio={anio}
          onCerrar={() => setAlta(false)}
          onGuardar={async (nuevo) => {
            setAlta(false);
            await operar(
              () => agregarFeriado(nuevo),
              `Se agregó el feriado del ${p2(nuevo.dia)}/${p2(nuevo.mes)}.`,
            );
          }}
        />
      )}

      <Modal
        abierto={aBorrar !== null}
        titulo="Quitar el feriado"
        onCerrar={() => setABorrar(null)}
        pie={
          <>
            <button type="button" className="plano" onClick={() => setABorrar(null)}>
              Cancelar
            </button>
            <button
              type="button"
              className="primaria"
              onClick={() => {
                const f = aBorrar;
                setABorrar(null);
                if (f) {
                  void operar(
                    () => borrarFeriado(f.clave),
                    `Se quitó el feriado del ${formatear(f)}.`,
                  );
                }
              }}
            >
              <Trash2 size={15} aria-hidden="true" />
              Quitar
            </button>
          </>
        }
      >
        {aBorrar && (
          <>
            <p>
              {aBorrar.anio === null
                ? `Se va a quitar el ${formatear(aBorrar)} (${aBorrar.descripcion}) de todos los años, no solo de ${String(anio)}.`
                : `Se va a quitar el ${formatear(aBorrar)} (${aBorrar.descripcion}) del calendario.`}
            </p>
            <Aviso tono="atencion">
              Los períodos que se liquiden a partir de ahora van a contar ese día como hábil.
            </Aviso>
          </>
        )}
      </Modal>
    </section>
  );
}

/** Alta de un feriado: día, mes, si vale todos los años, y cómo se llama. */
function DialogoAlta({
  anio,
  onCerrar,
  onGuardar,
}: {
  readonly anio: number;
  readonly onCerrar: () => void;
  readonly onGuardar: (alta: {
    dia: number;
    mes: number;
    anio?: number;
    descripcion: string;
  }) => Promise<void>;
}): React.JSX.Element {
  const [dia, setDia] = useState(1);
  const [mes, setMes] = useState(1);
  const [todosLosAnios, setTodosLosAnios] = useState(true);
  const [descripcion, setDescripcion] = useState('');

  return (
    <Modal
      abierto
      titulo="Nuevo feriado"
      onCerrar={onCerrar}
      pie={
        <>
          <button type="button" className="plano" onClick={onCerrar}>
            Cancelar
          </button>
          <button type="submit" form="alta-feriado" className="primaria">
            <CalendarPlus size={15} aria-hidden="true" />
            Agregar
          </button>
        </>
      }
    >
      <form
        id="alta-feriado"
        className="formulario"
        onSubmit={(ev) => {
          ev.preventDefault();
          void onGuardar({
            dia,
            mes,
            descripcion,
            // `exactOptionalPropertyTypes`: la clave se omite, no va en undefined.
            ...(todosLosAnios ? {} : { anio }),
          });
        }}
      >
        <label className="campo">
          Día
          <input
            type="number"
            min={1}
            max={31}
            required
            value={dia}
            onChange={(ev) => setDia(Number(ev.target.value))}
          />
        </label>
        <label className="campo">
          Mes
          <select value={mes} onChange={(ev) => setMes(Number(ev.target.value))}>
            {MESES.map((nombre, i) => (
              <option key={nombre} value={i + 1}>
                {nombre}
              </option>
            ))}
          </select>
        </label>
        <label className="campo">
          Vigencia
          <select
            value={todosLosAnios ? 'siempre' : 'anio'}
            onChange={(ev) => setTodosLosAnios(ev.target.value === 'siempre')}
          >
            <option value="siempre">Todos los años</option>
            <option value="anio">{`Solo ${String(anio)}`}</option>
          </select>
        </label>
        <label className="campo">
          Descripción
          <input
            type="text"
            required
            maxLength={120}
            placeholder="Carnaval"
            value={descripcion}
            onChange={(ev) => setDescripcion(ev.target.value)}
          />
        </label>
      </form>
    </Modal>
  );
}
